export function getTodayIsoDate() {
  return new Date().toISOString().slice(0, 10)
}

export function calculateAgeFromBirthdate(birthDate) {
  if (!birthDate) return ""

  const date = new Date(birthDate)
  if (Number.isNaN(date.getTime())) return ""

  const today = new Date()
  let age = today.getFullYear() - date.getFullYear()
  const monthDiff = today.getMonth() - date.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
    age -= 1
  }

  return age >= 0 ? String(age) : ""
}

function mergeWithShape(initialState, source) {
  const safeSource = source && typeof source === "object" ? source : {}
  const result = { ...initialState }

  Object.keys(initialState).forEach((key) => {
    if (safeSource[key] !== undefined && safeSource[key] !== null) {
      result[key] = safeSource[key]
    }
  })

  return result
}

export function getInitialForm() {
  return {
    date: getTodayIsoDate(),
    firstName: "",
    lastName: "",
    phone: "",
    birthDate: "",
    occupation: "",
    education: "",
    address: "",
    maritalStatus: "",
    gender: "",
    recommended: "",
    recommendedBy: "",

    chiefComplaintTags: [],
    chiefComplaintOther: "",
    chiefComplaintDescription: "",
    painLevel: "",
    symptomSince: "",

    lastDentalVisitDate: "",
    lastDentalVisitReason: "",
    previousOrthodonticTreatment: "",
    previousDentalSurgeries: "",
    previousDentalTrauma: "",

    habits: [],
    habitsDuration: "",

    brushingFrequency: "",
    flossUse: "",
    mouthwashUse: "",
    regularCleaning: "",

    tmjIssues: [],
    tmjDetail: "",

    medicalConditions: [],
    medications: "",
    allergiesDetail: "",
    medicalNotes: "",

    observations: "",
    consentAccepted: false,
  }
}

export function getQuickCreateInitialForm() {
  return {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    birthDate: "",
  }
}

export function getInitialDiagnosisForm() {
  return {
    date: getTodayIsoDate(),
    diagnosisType: "PRELIMINARY",
    severity: "MEDIUM",
    mainDiagnosis: "",
    secondaryDiagnosis: "",
    affectedArea: "",
    tooth: "",
    surface: "",
    priority: "MEDIUM",
    recommendedTreatment: "",
    notes: "",
    linkedToOdontogram: true,
    sendToTreatmentPlan: false,
    validatedClinically: false,
  }
}

export function getInitialEndoForm() {
  return {
    date: getTodayIsoDate(),
    tooth: "",
    chiefComplaint: "",
    spontaneousPain: "",
    painType: "",
    urgencyLevel: "",
    coldTest: "",
    heatTest: "",
    percussion: "",
    palpation: "",
    mobility: "",
    probing: "",
    fistula: "",
    swelling: "",
    pulpDiagnosis: "",
    apicalDiagnosis: "",
    canalsObserved: "",
    restorationStatus: "",
    crackSuspicion: "",
    radiographicFindings: "",
    treatmentPlan: "",
    notes: "",
    consentAccepted: false,
  }
}

export function getInitialPerioForm() {
  return {
    date: getTodayIsoDate(),
    chiefComplaint: "",
    bleedingOnBrushing: "",
    spontaneousBleeding: "",
    halitosis: "",
    sensitivity: "",
    mobility: "",
    recession: "",
    plaqueLevel: "",
    calculusLevel: "",
    bleedingIndex: "",
    probingSummary: "",
    periodontalDiagnosis: "",
    riskLevel: "",
    previousPeriodontalTreatment: "",
    smokingStatus: "",
    diabetesStatus: "",
    maintenanceInterval: "",
    treatmentPlan: "",
    notes: "",
    consentAccepted: false,
  }
}

export function getInitialSurgeryForm() {
  return {
    date: getTodayIsoDate(),
    surgicalDiagnosis: "",
    procedureType: "",
    tooth: "",
    surgicalArea: "",
    anesthesiaType: "",
    anesthesiaDetail: "",
    flapType: "",
    incisionType: "",
    osteotomy: "",
    odontosection: "",
    extractionComplexity: "",
    sutures: "",
    findings: "",
    complications: "",
    indications: "",
    medicationsPostOp: "",
    followUpPlan: "",
    consentAccepted: false,
  }
}

export function getInitialOrthoForm() {
  return {
    date: getTodayIsoDate(),
    facialProfile: "",
    smileAnalysis: "",
    chiefComplaint: "",
    skeletalClass: "",
    molarClassRight: "",
    molarClassLeft: "",
    canineClassRight: "",
    canineClassLeft: "",
    overjet: "",
    overbite: "",
    crossbite: "",
    openbite: "",
    crowdingUpper: "",
    crowdingLower: "",
    spacingUpper: "",
    spacingLower: "",
    midlineUpper: "",
    midlineLower: "",
    tmjStatus: "",
    habits: "",
    previousOrthoTreatment: "",
    treatmentObjectives: "",
    treatmentPlan: "",
    notes: "",
    consentAccepted: false,
  }
}

export function getInitialPediatricForm() {
  return {
    date: getTodayIsoDate(),
    companionName: "",
    companionRelation: "",
    chiefComplaint: "",
    pregnancyNotes: "",
    birthType: "",
    birthComplications: "",
    breastfeeding: "",
    bottleUse: "",
    pacifierUse: "",
    fingerSucking: "",
    mouthBreathing: "",
    sugarIntake: "",
    eruptionHistory: "",
    brushingAssistance: "",
    fluorideExposure: "",
    cariesRisk: "",
    previousDentalExperience: "",
    behaviorDuringConsultation: "",
    treatmentPlan: "",
    notes: "",
    consentAccepted: false,
  }
}

export function mapBaseHistoryToForm(baseHistory, patient) {
  const initial = getInitialForm()

  const patientLayer = patient
    ? {
        firstName: patient.firstName || "",
        lastName: patient.lastName || "",
        phone: patient.phone || "",
        birthDate: patient.birthDate ? String(patient.birthDate).slice(0, 10) : "",
      }
    : {}

  const baseLayer = baseHistory?.dataJson && typeof baseHistory.dataJson === "object"
    ? baseHistory.dataJson
    : baseHistory && typeof baseHistory === "object"
      ? baseHistory
      : {}

  return mergeWithShape(
    {
      ...initial,
      ...patientLayer,
    },
    baseLayer
  )
}

export function mapDiagnosisInstanceToForm(instance) {
  return mergeWithShape(getInitialDiagnosisForm(), instance?.dataJson || {})
}

export function mapEndoInstanceToForm(instance) {
  return mergeWithShape(getInitialEndoForm(), instance?.dataJson || {})
}

export function mapPerioInstanceToForm(instance) {
  return mergeWithShape(getInitialPerioForm(), instance?.dataJson || {})
}

export function mapSurgeryInstanceToForm(instance) {
  return mergeWithShape(getInitialSurgeryForm(), instance?.dataJson || {})
}

export function mapOrthoInstanceToForm(instance) {
  return mergeWithShape(getInitialOrthoForm(), instance?.dataJson || {})
}

export function mapPediatricInstanceToForm(instance) {
  return mergeWithShape(getInitialPediatricForm(), instance?.dataJson || {})
}

export function mergeClinicalHistoryWithQrData(currentForm, qrData) {
  const next = { ...(currentForm || {}) }
  const safe = qrData && typeof qrData === "object" ? qrData : {}

  if (!next.firstName && safe.firstName) next.firstName = safe.firstName
  if (!next.lastName && safe.lastName) next.lastName = safe.lastName
  if (!next.phone && safe.phone) next.phone = safe.phone
  if (!next.birthDate && safe.birthDate) next.birthDate = String(safe.birthDate).slice(0, 10)
  if (!next.address && safe.address) next.address = safe.address
  if (!next.occupation && safe.occupation) next.occupation = safe.occupation
  if (!next.education && safe.education) next.education = safe.education
  if (!next.maritalStatus && safe.maritalStatus) next.maritalStatus = safe.maritalStatus
  if (!next.gender && safe.gender) next.gender = safe.gender
  if (!next.recommendedBy && safe.recommendedBy) next.recommendedBy = safe.recommendedBy

  if (Array.isArray(safe.chiefComplaintTags) && !next.chiefComplaintTags?.length) {
    next.chiefComplaintTags = [...safe.chiefComplaintTags]
  }

  if (!next.chiefComplaintDescription && safe.chiefComplaintDescription) {
    next.chiefComplaintDescription = safe.chiefComplaintDescription
  }

  if ((next.painLevel === "" || next.painLevel == null) && safe.painLevel != null) {
    next.painLevel = String(safe.painLevel)
  }

  if (Array.isArray(safe.habits) && !next.habits?.length) {
    next.habits = [...safe.habits]
  }

  if (Array.isArray(safe.medicalConditions) && !next.medicalConditions?.length) {
    next.medicalConditions = [...safe.medicalConditions]
  }

  if (!next.medications && safe.medications) next.medications = safe.medications
  if (!next.allergiesDetail && safe.allergiesDetail) next.allergiesDetail = safe.allergiesDetail
  if (!next.medicalNotes && safe.medicalNotes) next.medicalNotes = safe.medicalNotes

  return next
}

export function formatDateTimeLabel(value) {
  if (!value) return "Sin registro"

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "Sin registro"

  return date.toLocaleString("es-MX", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  })
}

export function getDateSortValue(value) {
  if (!value) return 0
  const timestamp = new Date(value).getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}

export function getTemplateCodeLabel(code) {
  const raw = String(code || "").toUpperCase()

  if (raw === "BASE_GENERAL") return "Historia base general"
  if (raw === "QR_PREINTAKE") return "Pre-registro QR"
  if (raw === "INITIAL_DIAGNOSIS") return "Diagnóstico inicial"
  if (raw === "ENDO") return "Historia de Endodoncia"
  if (raw === "PERIO") return "Historia de Periodoncia"
  if (raw === "SURGERY") return "Historia de Cirugía"
  if (raw === "ORTHO") return "Historia de Ortodoncia"
  if (raw === "PEDIATRIC") return "Historia de Odontopediatría"

  return raw || "Formulario clínico"
}

export function getTemplatePresentation(template) {
  const code = String(template?.code || "").toUpperCase()

  if (code === "INITIAL_DIAGNOSIS") {
    return {
      title: "Diagnóstico inicial",
      description: "Registro estructurado del diagnóstico inicial y conducta clínica sugerida.",
    }
  }

  if (code === "ENDO") {
    return {
      title: "Historia de Endodoncia",
      description: "Registro endodóntico estructurado.",
    }
  }

  if (code === "PERIO") {
    return {
      title: "Historia de Periodoncia",
      description: "Registro periodontal estructurado.",
    }
  }

  if (code === "SURGERY") {
    return {
      title: "Historia de Cirugía",
      description: "Registro para procedimientos quirúrgicos.",
    }
  }

  if (code === "ORTHO") {
    return {
      title: "Historia de Ortodoncia",
      description: "Registro orientado a ortodoncia.",
    }
  }

  if (code === "PEDIATRIC") {
    return {
      title: "Historia de Odontopediatría",
      description: "Registro pediátrico especializado.",
    }
  }

  return {
    title: template?.name || getTemplateCodeLabel(code),
    description: "Formulario clínico especializado.",
  }
}

export function getStatusPresentation(status) {
  const raw = String(status || "").toUpperCase()

  if (raw === "COMPLETED") return { label: "Completado", tone: "success" }
  if (raw === "SUBMITTED") return { label: "Enviado", tone: "info" }
  if (raw === "DRAFT") return { label: "Borrador", tone: "neutral" }
  if (raw === "REVIEWED") return { label: "Revisado", tone: "success" }
  if (raw === "PENDING") return { label: "Pendiente", tone: "warning" }
  if (raw === "LOCKED") return { label: "Bloqueado", tone: "warning" }

  return { label: raw || "Nuevo", tone: "neutral" }
}

export function getSourceLabel(source) {
  const raw = String(source || "").toUpperCase()

  if (raw === "DOCTOR") return "Doctor"
  if (raw === "CLINIC") return "Clínica"
  if (raw === "PATIENT_QR") return "Paciente por QR"
  if (raw === "PATIENT") return "Paciente"

  return raw || "Sistema"
}